import {
  pickSiteSocialLinksFromContent,
  SITE_SOCIAL_NETWORK_KEYS,
  type EditTarget,
  type LayoutBlock,
  type SiteSocialNetworkKey,
} from '@web-studio/shared-schemas';
import { SocialIcon } from '../../icons/SocialIcon';
import { SITE_SOCIAL_ICONS } from '../../icons/socialNetworks';
import { pickChrome } from '../../lib/siteChromeCopy';

const NETWORK_LABEL: Record<SiteSocialNetworkKey, string> = {
  facebook: SITE_SOCIAL_ICONS.facebook.title,
  instagram: SITE_SOCIAL_ICONS.instagram.title,
  tiktok: SITE_SOCIAL_ICONS.tiktok.title,
  x: SITE_SOCIAL_ICONS.x.title,
  youtube: SITE_SOCIAL_ICONS.youtube.title,
};

export type SiteSocialLinksSurface = 'footer' | 'nav';

export interface SiteSocialLinksProps {
  content: LayoutBlock['content'];
  locale: string;
  editable?: boolean;
  activeEditTarget?: EditTarget | null;
  /** Extra classes on the outer wrap. */
  className?: string;
  /** Show chrome heading above the icon row. */
  showHeading?: boolean;
  /**
   * BEM surface — footer columns vs hamburger drawer.
   * Defaults to `footer` for existing call sites.
   */
  surface?: SiteSocialLinksSurface;
}

/**
 * Curated social icon row from Local Presence (hydrated `social*` content keys).
 * Used by Footer arrangements and every Navigation hamburger drawer.
 *
 * Empty live site: render nothing. Empty studio edit: ghost icons + “Add profiles”
 * hit target that focuses Zone 4 `socialLinks` via `data-field-key="socialLinks"`.
 */
export function SiteSocialLinks({
  content,
  locale,
  editable = false,
  activeEditTarget = null,
  className,
  showHeading = false,
  surface = 'footer',
}: SiteSocialLinksProps) {
  const links = pickSiteSocialLinksFromContent(content, locale);
  const isEmpty = links.length === 0;
  if (isEmpty && !editable) {
    return null;
  }

  const isActive =
    Boolean(editable) &&
    activeEditTarget?.kind === 'content-field' &&
    (activeEditTarget.fieldKey === 'socialLinks' ||
      activeEditTarget.fieldKey.startsWith('social'));

  const editAttrs = editable
    ? ({ 'data-edit-child': 'content-field', 'data-field-key': 'socialLinks' } as const)
    : {};

  const wrapClass =
    surface === 'nav' ? 'ws-nav__social-wrap' : 'ws-footer__social-wrap';
  const navClass = surface === 'nav' ? 'ws-nav__social' : 'ws-footer__social';
  const listClass = surface === 'nav' ? 'ws-nav__social-list' : 'ws-footer__social-list';
  const linkClass = surface === 'nav' ? 'ws-nav__social-link' : 'ws-footer__social-link';
  const emptyClass = surface === 'nav' ? 'ws-nav__social-empty' : 'ws-footer__social-empty';
  const headingClass = surface === 'nav' ? 'ws-nav__social-heading' : 'ws-footer__heading';

  return (
    <div
      className={[
        wrapClass,
        isEmpty ? `${wrapClass}--empty` : '',
        isActive ? 'ws-edit-child--active' : '',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...editAttrs}
    >
      {showHeading ? (
        <p className={headingClass}>{pickChrome('footerSocial', locale)}</p>
      ) : null}
      <nav
        className={[navClass, isEmpty ? `${navClass}--empty` : ''].filter(Boolean).join(' ')}
        aria-label={
          isEmpty && editable
            ? pickChrome('footerSocialEmpty', locale)
            : pickChrome('footerSocial', locale)
        }
      >
        {isEmpty ? (
          <>
            <ul className={`${listClass} ${listClass}--ghost`} aria-hidden="true">
              {SITE_SOCIAL_NETWORK_KEYS.map((network) => (
                <li key={network}>
                  <span className={`${linkClass} ${linkClass}--ghost`}>
                    <SocialIcon icon={network} size="md" />
                  </span>
                </li>
              ))}
            </ul>
            <p className={emptyClass}>
              <span className={`${emptyClass}-cta`}>{pickChrome('footerSocialEmpty', locale)}</span>
              <span className={`${emptyClass}-hint`}>
                {pickChrome('footerSocialEmptyHint', locale)}
              </span>
            </p>
          </>
        ) : (
          <ul className={listClass}>
            {links.map(({ network, url }) => (
              <li key={network}>
                <a
                  className={linkClass}
                  href={editable ? '#' : url}
                  target={editable ? undefined : '_blank'}
                  rel={editable ? undefined : 'noopener noreferrer'}
                  aria-label={NETWORK_LABEL[network]}
                  onClick={
                    editable
                      ? (event) => {
                          event.preventDefault();
                        }
                      : undefined
                  }
                >
                  <SocialIcon icon={network} size="md" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </nav>
    </div>
  );
}
